import type { CSSProperties } from "react";
import type { HeartbeatRiskStatus } from "../../lib/services";
import { resolvePsychAssistHeartline } from "../../lib/psychassist-heartline";

interface PsychAssistHeartlineProps {
  status: HeartbeatRiskStatus | null;
  loading?: boolean;
  isMonitoring: boolean;
  isLockedOut: boolean;
  onDismissLockout?: () => void;
  variant?: "compact" | "panel";
}

export function PsychAssistHeartline({
  status,
  loading = false,
  isMonitoring,
  isLockedOut,
  onDismissLockout,
  variant = "compact",
}: PsychAssistHeartlineProps) {
  const presentation = resolvePsychAssistHeartline(status);
  const path =
    presentation.amplitude === "urgent"
      ? URGENT_TRACE
      : presentation.amplitude === "raised"
        ? RAISED_TRACE
        : CALM_TRACE;

  return (
    <div
      className={`psychassist-heartline relative min-w-[80px] flex-1 overflow-hidden rounded-md border border-[var(--fintheon-accent)]/10 bg-black/50 ${
        variant === "panel" ? "h-full min-h-16" : "h-7"
      }`}
      style={
        {
          "--psychassist-heartline-color": presentation.color,
          "--psychassist-heartline-duration": `${presentation.durationSeconds}s`,
        } as CSSProperties
      }
      data-heartline-state={presentation.amplitude}
      data-heartline-active={isMonitoring ? "true" : "false"}
      data-heartline-loading={loading ? "true" : "false"}
    >
      <div className="absolute inset-0 psychassist-heartline__grid" />
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 120 36"
        role="img"
        aria-label={`PsychAssist ${presentation.label}`}
        preserveAspectRatio="none"
      >
        <path
          className="psychassist-heartline__glow"
          d={path}
          pathLength={120}
        />
        <path
          className="psychassist-heartline__trace"
          d={path}
          pathLength={120}
        />
      </svg>
      {isLockedOut && (
        <div className="absolute inset-0 z-10 flex items-center justify-between bg-[var(--fintheon-bg)]/90 px-1.5">
          {onDismissLockout && (
            <button
              type="button"
              onClick={onDismissLockout}
              className="ml-auto text-[8px] text-[var(--fintheon-text)]/44 transition-colors hover:text-[var(--fintheon-text)]/82"
            >
              dismiss
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const CALM_TRACE = "M0 18 H18 L24 16 L30 20 L36 18 H120";
const RAISED_TRACE = "M0 18 H16 L21 12 L27 25 L34 8 L42 28 L50 18 H120";
const URGENT_TRACE =
  "M0 18 H12 L18 8 L24 30 L31 4 L39 32 L47 10 L56 24 L64 18 H120";
